import React, { useCallback, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuthStore } from '@/app/store/authStore'
import { routes } from '@/shared/config/routes'
import { Icon } from '@/shared/ui/Icon/Icon'
import { InvisibleButton } from '@/shared/ui/components/Button'
import { Typography, Flex } from 'antd'
import { theme } from '@/app/styles/theme'
import {
  AboutModal,
  HeaderContainer,
  LeftSection,
  Logo,
  LogoSection,
  NavButton,
  NavMenu,
  RightSection,
} from './styled'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import axios from 'axios'
import { receiverApi } from '@/shared/api/config'
import { useBodyScrollLock } from '@/app/hooks/useBodyScrollLock'
import { APP_VERSION } from '../../../../app-version'

const { Title, Text } = Typography

export const Header: React.FC = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { isUhd, isXl, isXxl, isSm, isXs, isMd } = useBreakpoint()
  const showText = isXl || isXxl || isUhd
  const isTablet = isXs || isSm || isMd
  const isMobile = isXs || isSm

  const [modalVisible, setModalVisible] = useState(false)
  const [versionBack, setVersionBack] = useState<{
    version: string
    date: string
    commit?: string
  } | null>(null)
  const [loading, setLoading] = useState(false)

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return ''
    try {
      const [year, month, day] = dateString.split('-')
      return `${day}.${month}.${year}`
    } catch {
      return dateString
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('❌ Logout error:', error)
    }
  }
  const isActive = (path: string) => {
    return location.pathname === path
  }

  const getCurrentYear = () => {
    return new Date().getFullYear()
  }

  const handleLogoClick = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${receiverApi}/public/Version`, {
        timeout: 30000,
      })

      setVersionBack(response.data)
      setModalVisible(true)
    } catch (error) {
      console.error(error)

      setVersionBack(null)
      setModalVisible(true)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleModalClose = () => {
    setModalVisible(false)
    setVersionBack(null)
  }

  useBodyScrollLock(modalVisible)

  const currentYear = getCurrentYear()

  return (
    <HeaderContainer>
      <LeftSection gap={8} align="center">
        <InvisibleButton
          size="small"
          style={{ width: '32px' }}
          onClick={handleLogout}
          icon={<Icon name="user_logout" size={16} />}
        />
        <Title level={5}>{user?.username}</Title>
      </LeftSection>
      {!isMobile && (
        <LogoSection
          onClick={handleLogoClick}
          style={{ cursor: loading ? 'wait' : 'pointer' }}
        >
          <Logo
            src={isTablet ? '/images/logo-phone.svg' : '/images/logo.svg'}
            alt="ГЛОНАССSoft Transfer"
          />
        </LogoSection>
      )}
      <RightSection>
        <NavMenu>
          <NavButton
            type="invisible"
            icon={<Icon name="menu_wiki" size={16} fill="white" />}
            href="https://glonasssoft.ru/wiki/servis-migratsii-iz-wialon-v-glonasssoft/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {showText && 'Wiki'}
          </NavButton>
          <NavButton
            type={isActive(routes.tasks) ? 'primary' : 'invisible'}
            icon={
              <Icon
                name="menu_tasks"
                size={16}
                fill={isActive(routes.tasks) ? 'white' : theme.colors.primary}
              />
            }
            onClick={() => navigate(routes.tasks)}
          >
            {showText && 'Задачи'}
          </NavButton>
          <NavButton
            type={isActive(routes.transfer) ? 'primary' : 'invisible'}
            icon={
              <Icon
                name="menu_transfer"
                size={16}
                fill={
                  isActive(routes.transfer) ? 'white' : theme.colors.primary
                }
              />
            }
            onClick={() => navigate(routes.transfer)}
          >
            {showText && 'Перенос данных'}
          </NavButton>
        </NavMenu>
      </RightSection>
      <AboutModal
        title="О программе"
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={450}
        closeIcon={<Icon name="closed_modal" size={16} />}
      >
        <Flex vertical align="center" gap={24}>
          <Logo src="/images/logo.svg" alt="ГЛОНАССSoft Transfer" />
          <Text>
            Сайт разработчика:{' '}
            <Link target="_blank" to="https://glonasssoft.ru/">
              glonasssoft.ru
            </Link>
          </Text>
          <Flex vertical gap={4}>
            <Text strong>Front-end</Text>
            <Text>
              {APP_VERSION.value} ({APP_VERSION.date})
            </Text>
          </Flex>
          <Flex vertical gap={4}>
            <Text strong>Back-end</Text>
            <Text>
              {versionBack?.version} ({formatDate(versionBack?.date)})
            </Text>
          </Flex>
          <Text>Copyright © {currentYear} ГЛОНАСССофт</Text>
        </Flex>
      </AboutModal>
    </HeaderContainer>
  )
}
